import { browserName, isMobile } from "react-device-detect";
import { getProfile, getTokenPayload } from "../security";

const CLIENT_ID_STORAGE_KEY = "eventsapp:pwa:client-id";
const SENT_EVENT_STORAGE_KEY = "eventsapp:pwa:sent-events";
const CLIENT_ID_COOKIE_KEY = "eventsapp_pwa_client_id";
const CLIENT_ID_COOKIE_MAX_AGE_SECONDS = 15552000; // cookie will expire after six months 6months (~180 days) 180* 24*60*60

const getCookieValue = (cookieName) => {
  if (typeof document === "undefined") {
    return null;
  }

  const escapedName = cookieName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const setCookieValue = (cookieName, cookieValue) => {
  if (typeof document === "undefined") {
    return;
  }

  document.cookie = `${cookieName}=${encodeURIComponent(cookieValue)}; Max-Age=${CLIENT_ID_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax`;
};

const getConfigs = () => {
  if (typeof window === "undefined") {
    return {};
  }

  return window._CONFIGS || {};
};

const getBackendApi = () => {
  if (process.env.NODE_ENV === "development") {
    return process.env.REACT_APP_BACKEND_API || "";
  }

  return getConfigs().REACT_APP_BACKEND_API || "";
};

export const getApplicationVersion = () => {
  const runtimeConfig = getConfigs();
  return (
    process.env.REACT_APP_PWA_VERSION ||
    runtimeConfig.REACT_APP_PWA_VERSION ||
    process.env.npm_package_version ||
    "0.0.0-dev"
  );
};

const getRollbackVersion = () => {
  const runtimeConfig = getConfigs();
  return process.env.REACT_APP_PWA_ROLLBACK_VERSION || runtimeConfig.REACT_APP_PWA_ROLLBACK_VERSION || null;
};

const getDisplayMode = () => {
  if (typeof window === "undefined") {
    return "browser";
  }

  if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) {
    return "standalone";
  }

  if (window.matchMedia("(display-mode: fullscreen)").matches) {
    return "fullscreen";
  }

  return "browser";
};

const getDeviceCategory = () => {
  if (isMobile) {
    return "mobile";
  }

  return "desktop";
};

const getTelemetryUserId = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const profile = getProfile();
    if (profile?.userId) {
      return profile.userId;
    }
  } catch (_error) {
    // Continue with token payload fallback.
  }

  try {
    const tokenPayload = getTokenPayload();
    if (tokenPayload?.sub) {
      return tokenPayload.sub;
    }
  } catch (_error) {
    // Leave anonymous if auth context is unavailable.
  }

  return null;
};

export const getClientId = () => {
  if (typeof window === "undefined") {
    return "server";
  }

  try {
    const existing = window.localStorage.getItem(CLIENT_ID_STORAGE_KEY);
    if (existing) {
      setCookieValue(CLIENT_ID_COOKIE_KEY, existing);
      return existing;
    }
  } catch (_error) {
    // Ignore storage access errors and continue with cookie fallback.
  }

  const cookieClientId = getCookieValue(CLIENT_ID_COOKIE_KEY);
  if (cookieClientId) {
    try {
      window.localStorage.setItem(CLIENT_ID_STORAGE_KEY, cookieClientId);
    } catch (_error) {
      // localStorage may be unavailable; cookie is enough as fallback.
    }
    return cookieClientId;
  }

  const clientId =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `eventsapp-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  try {
    window.localStorage.setItem(CLIENT_ID_STORAGE_KEY, clientId);
  } catch (_error) {
    // localStorage may be unavailable.
  }
  setCookieValue(CLIENT_ID_COOKIE_KEY, clientId);
  return clientId;
};

const getSentEventKeys = () => {
  if (typeof window === "undefined") {
    return new Set();
  }

  try {
    const raw = window.localStorage.getItem(SENT_EVENT_STORAGE_KEY);
    if (!raw) {
      return new Set();
    }
    return new Set(JSON.parse(raw));
  } catch (_error) {
    return new Set();
  }
};

const markEventAsSent = (key) => {
  if (typeof window === "undefined") {
    return;
  }

  const current = getSentEventKeys();
  current.add(key);
  window.localStorage.setItem(SENT_EVENT_STORAGE_KEY, JSON.stringify([...current]));
};

const isEventAlreadySent = (key) => getSentEventKeys().has(key);

const buildMetricEnvelope = ({ appName, metricType, swVersion, metadata = {} }) => {
  const appVersion = getApplicationVersion();
  const userId = getTelemetryUserId();

  return {
    message: `PWA_METRIC:${metricType}`,
    level: "INFO",
    mobile: isMobile,
    browser: browserName || "Unknown",
    stackTrace: JSON.stringify({
      appName,
      appVersion,
      swVersion: swVersion || "unknown",
      rollbackVersion: getRollbackVersion(),
      displayMode: getDisplayMode(),
      installContext: getDeviceCategory(),
      browser: browserName || "Unknown",
      isMobile,
      clientId: getClientId(),
      userId,
      metadata,
    }),
    errorTime: new Date().toISOString(),
  };
};

const postMetric = async (payload, appName) => {
  const base = getBackendApi();
  if (!base) {
    return;
  }

  const normalizedBase = base.endsWith("/") ? base.slice(0, -1) : base;
  const endpoint = `${normalizedBase}/system/log/application/errors/events`;

  try {
    await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (_error) {
    // Keep telemetry failures silent to avoid impacting app behavior.
  }
};

const clearServiceWorkerState = async () => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));

    if (window.caches && window.caches.keys) {
      const cacheKeys = await window.caches.keys();
      await Promise.all(cacheKeys.map((key) => window.caches.delete(key)));
    }
  } catch (_error) {
    // Rollback cleanup is best effort.
  }
};

export const trackPwaMetric = async ({
  appName = "events-app",
  metricType,
  swVersion,
  metadata,
  dedupeKey,
}) => {
  if (!metricType) {
    return;
  }

  const key = dedupeKey || `${metricType}:${getApplicationVersion()}:${swVersion || "unknown"}`;
  if (isEventAlreadySent(key)) {
    return;
  }

  const payload = buildMetricEnvelope({ appName, metricType, swVersion, metadata });
  await postMetric(payload, appName);
  markEventAsSent(key);
};

export const handlePwaVersion = async ({ appName = "events-app", swVersion }) => {
  if (!swVersion) {
    return { rollbackApplied: false };
  }

  await trackPwaMetric({
    appName,
    metricType: "PWA_VERSION_DETECTED",
    swVersion,
    dedupeKey: `PWA_VERSION_DETECTED:${swVersion}:${getClientId()}`,
  });

  const rollbackVersion = getRollbackVersion();
  if (rollbackVersion && rollbackVersion === swVersion) {
    await trackPwaMetric({
      appName,
      metricType: "PWA_ROLLBACK_TRIGGERED",
      swVersion,
      metadata: { rollbackVersion },
      dedupeKey: `PWA_ROLLBACK_TRIGGERED:${swVersion}:${getClientId()}`,
    });

    await clearServiceWorkerState();
    return { rollbackApplied: true };
  }

  return { rollbackApplied: false };
};

export const setupPwaTelemetry = ({ appName = "events-app" } = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  trackPwaMetric({
    appName,
    metricType: "PWA_CLIENT_SEEN",
    metadata: {
      userAgent: navigator.userAgent,
      online: navigator.onLine,
    },
    dedupeKey: `PWA_CLIENT_SEEN:${getApplicationVersion()}:${getClientId()}`,
  });

  window.addEventListener("appinstalled", () => {
    trackPwaMetric({
      appName,
      metricType: "PWA_APP_INSTALLED",
      dedupeKey: `PWA_APP_INSTALLED:${getApplicationVersion()}:${getClientId()}`,
    });
  });

  window.addEventListener("eventsapp:installable", () => {
    trackPwaMetric({
      appName,
      metricType: "PWA_INSTALL_PROMPT_AVAILABLE",
      dedupeKey: `PWA_INSTALL_PROMPT_AVAILABLE:${getApplicationVersion()}:${getClientId()}`,
    });
  });
};
